/**
 * Bulk actions on picked backlog items, the same in the tree and the table: the bottom action
 * bar (Set status, Priority, Owner, Start mission or Plan missions, Delete), the delete
 * confirmation, and, inline where it is placed, what a change left undone and why (never a
 * toast for that).
 */
import { CircleDashed, Rocket, SignalHigh, Trash2, UserRound } from '@orgus/ui/icons';
import { useState } from 'react';
import { ITEM_PRIORITIES, ITEM_PRIORITY_LABEL, statusLabel } from '@orgus/protocol';
import type { BacklogItem, Id, ItemBulkPatch } from '@orgus/protocol';
import { Banner, BulkBar, Button, ConfirmDialog, ErrorState, plural, toast } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import { bulkDelete, bulkUpdate } from '../requirements/data.ts';
import { sharedStatuses } from '../requirements/model.ts';
import { canStartMission } from '../requirements/TreeView.tsx';
import { createMission } from './data.ts';
import { goalFromItem } from './model.ts';
import type { MissionRow } from './model.ts';

interface Skipped {
  what: string;
  skipped: { id: Id; reason: string }[];
}

export function BacklogBulk({
  picked,
  allItems,
  rows,
  owners,
  total,
  onSelectAll,
  onClear,
  onStart,
  onStartMany,
  deleting,
  onDeleting,
}: {
  picked: readonly BacklogItem[];
  allItems: readonly BacklogItem[];
  rows: readonly MissionRow[];
  owners: readonly string[];
  /** Rows on screen, for Select all. */
  total: number;
  onSelectAll?: () => void;
  onClear: () => void;
  onStart: (item: BacklogItem) => void;
  /** Start several at once (Start mission, one per item, one choice of machine and options). */
  onStartMany?: (items: BacklogItem[]) => void;
  /** Items whose delete waits for confirmation (from the bar or a row's menu). */
  deleting: BacklogItem[] | null;
  onDeleting: (items: BacklogItem[] | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [skipped, setSkipped] = useState<Skipped | null>(null);
  const keyOf = (id: Id) => allItems.find((i) => i.id === id)?.key ?? id;
  const startable = picked.filter((i) => canStartMission(i, rows));

  const run = async (what: string, fn: () => Promise<{ skipped: { id: Id; reason: string }[] }>, done: (n: number) => string, n: number) => {
    setBusy(true);
    setError(null);
    setSkipped(null);
    try {
      const r = await fn();
      if (r.skipped.length) setSkipped({ what, skipped: r.skipped });
      if (n - r.skipped.length > 0) toast.success(done(n - r.skipped.length));
      if (!r.skipped.length) onClear();
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  const patch = (p: ItemBulkPatch['patch'], what: string) => void run(what, () => bulkUpdate({ ids: picked.map((i) => i.id), patch: p }), (n) => `${what}: ${plural(n, 'item')}`, picked.length);

  // Several start together only within one project: one machine and checkout serve them all.
  const oneProject = new Set(startable.map((i) => i.projectId)).size <= 1;
  const startMissions = () => {
    if (startable.length === 1) onStart(startable[0]!);
    else if (onStartMany && oneProject) onStartMany(startable);
    else return planMissions();
    onClear();
  };
  /** Each gets a planned mission, ready to start from the Missions tab. */
  const planMissions = () => {
    void run(
      'Missions planned',
      async () => {
        const out: { id: Id; reason: string }[] = [];
        for (const it of startable) {
          try {
            await createMission({ projectId: it.projectId, itemId: it.id, title: `${it.key} ${it.title}`.slice(0, 200), goal: goalFromItem(it) });
          } catch (e) {
            out.push({ id: it.id, reason: toUiError(e).why });
          }
        }
        return { skipped: out };
      },
      (n) => `${plural(n, 'mission')} planned. Start them from the Missions tab.`,
      startable.length,
    );
  };

  const statuses = sharedStatuses(picked).filter((s) => s !== 'triage');
  const defectsOnly = picked.length > 0 && picked.every((i) => i.kind === 'defect');
  const one = deleting?.length === 1 ? deleting[0]! : null;
  return (
    <>
      {error && <ErrorState variant="inline" error={error} action={<Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />}
      {skipped && (
        <Banner
          tone="attention"
          title={`${skipped.what}: ${plural(skipped.skipped.length, 'item')} left as they were`}
          actions={
            <Button variant="ghost" size="sm" onClick={() => setSkipped(null)}>
              Dismiss
            </Button>
          }
        >
          {skipped.skipped
            .slice(0, 3)
            .map((s) => `${keyOf(s.id)}: ${s.reason}`)
            .join(' ')}
          {skipped.skipped.length > 3 ? ` And ${plural(skipped.skipped.length - 3, 'more')}.` : ''}
        </Banner>
      )}
      <BulkBar
        count={picked.length}
        noun="item"
        total={total}
        onSelectAll={onSelectAll}
        onClear={onClear}
        actions={[
          {
            id: 'status',
            label: 'Set status',
            icon: <CircleDashed />,
            disabled: busy || !statuses.length,
            menu: statuses.map((s) => ({ id: s, label: statusLabel(defectsOnly ? 'defect' : 'story', s), onSelect: () => patch({ status: s }, 'Status changed') })),
          },
          {
            id: 'priority',
            label: 'Priority',
            icon: <SignalHigh />,
            disabled: busy,
            menu: ITEM_PRIORITIES.map((p) => ({ id: String(p), label: ITEM_PRIORITY_LABEL[p], onSelect: () => patch({ priority: p }, 'Priority changed') })),
          },
          {
            id: 'owner',
            label: 'Owner',
            icon: <UserRound />,
            disabled: busy,
            menu: [...owners.map((o) => ({ id: o, label: o, onSelect: () => patch({ owner: o }, 'Owner changed') })), { id: '', label: 'Unassigned', onSelect: () => patch({ owner: null }, 'Owner cleared') }],
          },
          {
            id: 'start',
            label: startable.length > 1 && onStartMany && oneProject ? `Start ${plural(startable.length, 'mission')}` : startable.length > 1 ? `Plan ${plural(startable.length, 'mission')}` : 'Start mission',
            icon: <Rocket />,
            disabled: busy || !startable.length,
            hint: !startable.length ? 'None of these is an open story, task or defect without a mission' : startable.length > 1 && !oneProject ? 'They are in different projects, so each gets a planned mission' : undefined,
            onClick: startMissions,
            // Planning is the quieter way: a mission each, started later from the Missions tab.
            ...(startable.length > 1 && onStartMany && oneProject ? { menu: [{ id: 'plan', label: `Plan ${plural(startable.length, 'mission')} for later`, icon: <Rocket />, onSelect: planMissions }] } : {}),
          },
          { id: 'delete', label: 'Delete', icon: <Trash2 />, danger: true, disabled: busy, onClick: () => onDeleting([...picked]) },
        ]}
      />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && onDeleting(null)}
        title={one ? `Delete ${one.key} "${one.title}"?` : `Delete ${plural(deleting?.length ?? 0, 'item')}?`}
        description="Items with other children, or with missions that keep their history, stay and say why."
        confirmLabel={one ? 'Delete item' : `Delete ${plural(deleting?.length ?? 0, 'item')}`}
        cancelLabel={one ? 'Keep it' : 'Keep them'}
        onConfirm={async () => {
          const list = deleting ?? [];
          setSkipped(null);
          const r = await bulkDelete(list.map((i) => i.id));
          if (r.skipped.length) setSkipped({ what: 'Delete', skipped: r.skipped });
          if (r.deleted.length) toast.success(`Deleted ${plural(r.deleted.length, 'item')}`);
          onClear();
        }}
      />
    </>
  );
}
